// Photos taken on a job: shrunk on the phone, kept in the local database and
// uploaded when there is signal (like the outbox, but one file at a time).
// The id is made here, so a resend after a dropped connection is the same
// photo on the server, not a second one. A photo leaves the phone only once
// the server has it.
import { db } from './db.js';
import { api, ApiError } from '../api/client.js';

const MAX_SIDE = 1600;
const listeners = new Set();
const notify = () => listeners.forEach((fn) => fn());
export const subscribePhotos = (fn) => { listeners.add(fn); return () => listeners.delete(fn); };

// When it was taken, UK time, DD/MM/YYYY HH:mm.
const stampFmt = new Intl.DateTimeFormat('en-GB', { timeZone: 'Europe/London', day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
export const stampText = (when, label) => [stampFmt.format(when).replace(',', ''), label].filter(Boolean).join(' · ');

// The date and time (and the job) printed along the bottom, so it stays with
// the photo wherever it goes. A camera photo is stamped now; one chosen from
// the gallery with the time the phone saved it.
function stamp(ctx, w, h, text) {
  const size = Math.max(16, Math.round(Math.min(w, h) * 0.04));
  const pad = Math.round(size * 0.6);
  ctx.font = `600 ${size}px system-ui, -apple-system, "Segoe UI", Roboto, Arial, sans-serif`;
  ctx.textBaseline = 'middle';
  const band = size + pad * 2;
  ctx.fillStyle = 'rgba(0, 0, 0, 0.55)';
  ctx.fillRect(0, h - band, w, band);
  ctx.fillStyle = '#ffffff';
  ctx.fillText(text, pad, h - band / 2, w - pad * 2);
}

// A camera photo (often 4-8 MB) as a ~300 KB JPEG, the right way up, with
// the date and time on it (and `label`, the job reference).
export async function shrink(file, { label = null } = {}) {
  try {
    const bmp = await createImageBitmap(file, { imageOrientation: 'from-image' });
    const scale = Math.min(1, MAX_SIDE / Math.max(bmp.width, bmp.height));
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(bmp.width * scale);
    canvas.height = Math.round(bmp.height * scale);
    const ctx = canvas.getContext('2d');
    ctx.drawImage(bmp, 0, 0, canvas.width, canvas.height);
    const taken = file.lastModified && Math.abs(Date.now() - file.lastModified) < 365 * 864e5 ? new Date(file.lastModified) : new Date();
    stamp(ctx, canvas.width, canvas.height, stampText(taken, label));
    bmp.close?.();
    const blob = await new Promise((ok) => canvas.toBlob(ok, 'image/jpeg', 0.8));
    if (blob) return blob;
  } catch { /* fall through */ }
  if (file.type === 'image/jpeg') return file;
  throw new Error('That photo couldn’t be read. Try taking it again.');
}

// Queue photos (already shrunk) for a job, then try to send them.
// incidentId links incident photos to their report.
export async function queuePhotos(bookingId, blobs, kind, at = new Date().toISOString(), incidentId = null) {
  if (!blobs.length) return;
  await db.photos.bulkAdd(blobs.map((blob) => ({ id: crypto.randomUUID(), bookingId, kind, incidentId, blob, takenAt: at, attempts: 0 })));
  notify();
  flushPhotos();
}

// Photos for something other than a booking (a Network Plus survey): `key`
// groups them on the phone (e.g. 'survey:12') and `path` is the endpoint each
// one is PUT to, as path/<photo id>. slot: what the photo is of (a survey's
// photo slots), sent along with it.
export async function queuePhotosTo(key, path, blobs, at = new Date().toISOString(), slot = null) {
  if (!blobs.length) return;
  await db.photos.bulkAdd(blobs.map((blob) => ({ id: crypto.randomUUID(), bookingId: key, kind: 'survey', slot, path, blob, takenAt: at, attempts: 0 })));
  notify();
  flushPhotos();
}

export async function dropPhoto(id) {
  await db.photos.delete(id);
  notify();
}

export const pendingPhotos = () => db.photos.count();

// Remove a step photo taken by mistake: off the phone if it hasn't gone yet,
// and from the server (queued; the server hides it even if the photo gets
// there after this does).
export async function removePhoto(outbox, bookingId, photo) {
  await db.photos.delete(photo.id);
  notify();
  await outbox.enqueue('job.photo.remove', { bookingId, photoId: photo.id, kind: photo.kind });
}

let running = null;
let retry = null;
export function flushPhotos() {
  if (!running) running = send().finally(() => { running = null; });
  return running;
}

async function send() {
  for (const p of await db.photos.orderBy('takenAt').toArray()) {
    try {
      const url = p.path
        ? `${p.path}/${p.id}?at=${encodeURIComponent(p.takenAt)}${p.slot ? `&slot=${p.slot}` : ''}`
        : `/my/jobs/${p.bookingId}/photos/${p.id}?kind=${p.kind}&at=${encodeURIComponent(p.takenAt)}${p.incidentId ? `&incident=${p.incidentId}` : ''}`;
      await api(url, { method: 'PUT', raw: p.blob });
      await db.photos.delete(p.id);
      notify();
    } catch (e) {
      // The server will never take it (not their team's job, not a photo):
      // keeping it would block the queue for good.
      if (e instanceof ApiError && e.status >= 400 && e.status < 500 && ![401, 408, 429].includes(e.status)) {
        await db.photos.delete(p.id);
        notify();
        continue;
      }
      // No signal, server busy, or photos not set up yet: try again later.
      clearTimeout(retry);
      retry = setTimeout(flushPhotos, Math.min(5 * 60_000, 15_000 * 2 ** Math.min(p.attempts, 5)));
      await db.photos.update(p.id, { attempts: p.attempts + 1 });
      return;
    }
  }
}
